/**
 * Enhanced component for displaying a list of tee times for a selected date and course
 */
import React, { useState } from 'react';
import { useTeeTime } from '../hooks/useTeeTime';
import { TeeTime } from '../types';
import { formatDateLong } from '../utils/dateUtils';
import TeeTimeCard from './TeeTimeCard';
import '../styles/TeeTimeList.css';

/**
 * Filter options for tee times
 */
interface FilterOptions {
  timeRange: {
    start: number; // Hour in 24h format
    end: number;   // Hour in 24h format
  };
  minAvailableSpots: number;
  showSpecialsOnly: boolean;
}

/**
 * TeeTimeList component displays available tee times for the selected date and course
 * @returns React component
 */
const TeeTimeList: React.FC = () => {
  const { 
    selectedDate, 
    selectedTeeTime, 
    setSelectedTeeTime, 
    weeklySchedule, 
    loading,
    selectedCourse 
  } = useTeeTime();
  
  // State for filter options
  const [filters, setFilters] = useState<FilterOptions>({
    timeRange: { start: 0, end: 24 }, // Default to all hours
    minAvailableSpots: 1,             // At least 1 spot available
    showSpecialsOnly: false,          // Show all tee times
  });
  
  const [showFilters, setShowFilters] = useState(false);
  
  // Find the schedule for the selected date and course
  const dailySchedule = selectedCourse && weeklySchedule.find(schedule => 
    schedule.date.toDateString() === selectedDate.toDateString() && 
    schedule.courseId === selectedCourse.id
  );
  
  // Handler for selecting a tee time
  const handleSelectTeeTime = (teeTime: TeeTime) => {
    setSelectedTeeTime(teeTime);
  };
  
  /**
   * Updates filter values
   * @param key Filter key to update
   * @param value New value
   */
  const updateFilter = <K extends keyof FilterOptions>(key: K, value: FilterOptions[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  /**
   * Checks if a tee time matches the current filters
   * @param teeTime The tee time to check
   * @returns True if the tee time matches filters
   */
  const matchesFilters = (teeTime: TeeTime): boolean => {
    // Parse hours from time string (e.g., "14:30" -> 14)
    const hour = parseInt(teeTime.startTime.split(':')[0], 10);
    
    // Check time range
    if (hour < filters.timeRange.start || hour >= filters.timeRange.end) {
      return false;
    }
    
    // Check availability
    if (teeTime.availableSpots < filters.minAvailableSpots) {
      return false;
    }
    
    // Check special offers
    if (filters.showSpecialsOnly && !teeTime.specialOffer) {
      return false;
    }
    
    return true;
  };
  
  /**
   * Resets all filters to default values
   */
  const resetFilters = () => {
    setFilters({
      timeRange: { start: 0, end: 24 },
      minAvailableSpots: 1,
      showSpecialsOnly: false,
    });
  };
  
  // Group tee times by time period
  const groupTeeTimesByPeriod = (teeTimes: TeeTime[]) => {
    const filteredTeeTimes = teeTimes.filter(matchesFilters);
    
    const periods = [
      { name: 'Morning', teeTimes: [] as TeeTime[] },
      { name: 'Afternoon', teeTimes: [] as TeeTime[] },
      { name: 'Evening', teeTimes: [] as TeeTime[] },
    ];
    
    filteredTeeTimes.forEach(teeTime => {
      const hour = parseInt(teeTime.startTime.split(':')[0], 10);
      
      if (hour < 12) {
        periods[0].teeTimes.push(teeTime);
      } else if (hour < 16) {
        periods[1].teeTimes.push(teeTime);
      } else {
        periods[2].teeTimes.push(teeTime);
      }
    });
    
    return periods.filter(period => period.teeTimes.length > 0);
  };
  
  if (loading) {
    return <div className="tee-time-list-loading">Loading tee times...</div>;
  }
  
  if (!selectedCourse) {
    return (
      <div className="tee-time-list-empty">
        <h2>Please select a golf course</h2>
      </div>
    );
  }
  
  if (!dailySchedule || dailySchedule.teeTimes.length === 0) {
    return (
      <div className="tee-time-list-empty">
        <h2>No tee times available for {formatDateLong(selectedDate)} at {selectedCourse.name}</h2>
      </div>
    );
  }
  
  const groupedTeeTimes = groupTeeTimesByPeriod(dailySchedule.teeTimes);
  const hasResults = groupedTeeTimes.some(group => group.teeTimes.length > 0);
  
  return (
    <div className="tee-time-list">
      <div className="tee-time-list-header">
        <h2>Tee Times for {formatDateLong(selectedDate)}</h2>
        <button 
          className="filter-toggle-button" 
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Filter Tee Times'}
        </button>
      </div>
      
      {showFilters && (
        <div className="tee-time-filters">
          <div className="filter-section">
            <h3>Time of Day</h3>
            <div className="time-range-selector">
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.timeRange.start <= 6 && filters.timeRange.end > 12}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateFilter('timeRange', { ...filters.timeRange, start: 6, end: 12 });
                    } else {
                      updateFilter('timeRange', { ...filters.timeRange, start: 12 });
                    }
                  }}
                />
                Morning (6AM - 12PM)
              </label>
              
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.timeRange.start <= 12 && filters.timeRange.end > 16}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateFilter('timeRange', { ...filters.timeRange, start: Math.min(filters.timeRange.start, 12), end: Math.max(filters.timeRange.end, 16) });
                    } else {
                      if (filters.timeRange.start >= 12 && filters.timeRange.start < 16) {
                        updateFilter('timeRange', { ...filters.timeRange, start: 16 });
                      } else if (filters.timeRange.end > 12 && filters.timeRange.end <= 16) {
                        updateFilter('timeRange', { ...filters.timeRange, end: 12 });
                      }
                    }
                  }}
                />
                Afternoon (12PM - 4PM)
              </label>
              
              <label>
                <input 
                  type="checkbox" 
                  checked={filters.timeRange.start <= 16 && filters.timeRange.end > 16}
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateFilter('timeRange', { ...filters.timeRange, start: Math.min(filters.timeRange.start, 16), end: 24 });
                    } else {
                      if (filters.timeRange.start >= 16) {
                        updateFilter('timeRange', { ...filters.timeRange, start: 24 });
                      } else {
                        updateFilter('timeRange', { ...filters.timeRange, end: 16 });
                      }
                    }
                  }}
                />
                Evening (After 4PM)
              </label>
            </div>
          </div>
          
          <div className="filter-section">
            <h3>Availability</h3>
            <div className="availability-selector">
              <label>
                <input 
                  type="radio" 
                  name="minSpots"
                  checked={filters.minAvailableSpots === 1}
                  onChange={() => updateFilter('minAvailableSpots', 1)}
                />
                Any available (1+)
              </label>
              
              <label>
                <input 
                  type="radio" 
                  name="minSpots"
                  checked={filters.minAvailableSpots === 2}
                  onChange={() => updateFilter('minAvailableSpots', 2)}
                />
                At least 2 spots
              </label>
              
              <label>
                <input 
                  type="radio" 
                  name="minSpots"
                  checked={filters.minAvailableSpots === 4}
                  onChange={() => updateFilter('minAvailableSpots', 4)}
                />
                Full foursome (4 spots)
              </label>
            </div>
          </div>
          
          <div className="filter-section">
            <h3>Special Offers</h3>
            <label>
              <input 
                type="checkbox" 
                checked={filters.showSpecialsOnly}
                onChange={(e) => updateFilter('showSpecialsOnly', e.target.checked)}
              />
              Show only tee times with special offers
            </label>
          </div>
          
          <div className="filter-actions">
            <button className="reset-filters-button" onClick={resetFilters}>
              Reset Filters
            </button>
          </div>
        </div>
      )}
      
      {hasResults ? (
        <div className="tee-time-groups">
          {groupedTeeTimes.map(group => (
            <div key={group.name} className="tee-time-group">
              <h3 className="group-title">{group.name}</h3>
              <div className="tee-time-cards">
                {group.teeTimes.map(teeTime => (
                  <TeeTimeCard
                    key={teeTime.id}
                    teeTime={teeTime}
                    course={selectedCourse}
                    isSelected={selectedTeeTime?.id === teeTime.id}
                    onSelect={handleSelectTeeTime}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <p>No tee times match your filters. Try adjusting your criteria.</p>
          <button className="reset-filters-button" onClick={resetFilters}>
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default TeeTimeList;
